import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_CONST } from '../const';

const CACHE_DURATION = 5 * 60 * 1000;

const getAuthToken = () => localStorage.getItem('authToken');

export const fetchUsers = createAsyncThunk(
  'userManagement/fetchUsers',
  async ({ page = 1, pageSize = 100, search = '', organization = null, designation = null }, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue({ message: 'No auth token', sessionExpired: true });
      }

      const requestBody = { page, pageSize };
      if (search) requestBody.search = search;
      if (organization) requestBody.organization = organization;
      if (designation) requestBody.designation = designation;

      const response = await fetch(API_CONST.GET_USER_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (response.status === 401 || response.status === 403) {
        return rejectWithValue({ message: 'Session expired', sessionExpired: true });
      }

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      return {
        users: result.Response.map(user => ({
          ...user,
          status: user.status || 'Active',
        })),
        pagination: result.pagination,
      };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchFilterOptions = createAsyncThunk(
  'userManagement/fetchFilterOptions',
  async ({ role }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const cached = state.userManagement.filterOptionsCache[role];
      
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return { role, options: cached.options, fromCache: true };
      }

      const token = getAuthToken();
      if (!token) {
        return rejectWithValue({ message: 'No auth token', sessionExpired: true });
      }

      const response = await fetch(`${API_CONST.GET_USER_FILTER_OPTIONS}?role=${encodeURIComponent(role)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401 || response.status === 403) {
        return rejectWithValue({ message: 'Session expired', sessionExpired: true });
      }

      if (!response.ok) {
        throw new Error('Failed to fetch filter options');
      }

      const result = await response.json();
      const orgFilter = result.filters.find(f => f.field === 'organization');
      const desFilter = result.filters.find(f => f.field === 'designation');

      return {
        role,
        options: {
          organizations: orgFilter?.options || [],
          designations: desFilter?.options || [],
        },
        fromCache: false,
      };
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

const initialState = {
  users: [],
  pagination: { current: 1, pageSize: 100, total: 0 },
  loading: false,
  error: null,
  filterOptions: { organizations: [], designations: [] },
  filterOptionsCache: {},
  activeTab: 'Customer',
  statusTab: 'Active',
  searchText: '',
  organizationFilter: null,
  designationFilter: null,
  lastFetchParams: null,
};

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
      state.organizationFilter = null;
      state.designationFilter = null;
    },
    setStatusTab: (state, action) => {
      state.statusTab = action.payload;
    },
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },
    setOrganizationFilter: (state, action) => {
      state.organizationFilter = action.payload;
    },
    setDesignationFilter: (state, action) => {
      state.designationFilter = action.payload;
    },
    clearFilters: (state) => {
      state.searchText = '';
      state.organizationFilter = null;
      state.designationFilter = null;
    },
    invalidateCache: (state) => {
      state.filterOptionsCache = {};
      state.lastFetchParams = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        if (action.payload.pagination) {
          state.pagination = {
            current: action.payload.pagination.page,
            pageSize: action.payload.pagination.pageSize,
            total: action.payload.pagination.total,
          };
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch users';
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        if (!action.payload.fromCache) {
          state.filterOptionsCache[action.payload.role] = {
            options: action.payload.options,
            timestamp: Date.now(),
          };
        }
        state.filterOptions = action.payload.options;
      })
      .addCase(fetchFilterOptions.rejected, (state, action) => {
        console.log('Failed to fetch filter options:', action.payload?.message);
      });
  },
});

export const {
  setActiveTab,
  setStatusTab,
  setSearchText,
  setOrganizationFilter,
  setDesignationFilter,
  clearFilters,
  invalidateCache,
  clearError,
} = userManagementSlice.actions;

export const selectUsers = (state) => state.userManagement.users;
export const selectPagination = (state) => state.userManagement.pagination;
export const selectLoading = (state) => state.userManagement.loading;
export const selectFilterOptions = (state) => state.userManagement.filterOptions;
export const selectActiveTab = (state) => state.userManagement.activeTab;
export const selectStatusTab = (state) => state.userManagement.statusTab;
export const selectSearchText = (state) => state.userManagement.searchText;
export const selectOrganizationFilter = (state) => state.userManagement.organizationFilter;
export const selectDesignationFilter = (state) => state.userManagement.designationFilter;

export default userManagementSlice.reducer;
